import React, { useState, useEffect, useRef, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { PayloadAction } from '@reduxjs/toolkit';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Spin, Row, Col, Button, Tooltip } from 'antd';
import queryString from 'query-string';
import pluralize from 'pluralize';
import moment from 'moment';

import {
  AssignmentStatusTypeEnum,
  AdminPagedAssignmentResponseModel,
  AssignmentTerminationReasonTypeEnum,
  WorkerTypeEnum,
} from 'api/adminApiClient';
import {
  getAssignmentListExcel,
  getAssignments,
} from 'redux/reducers/assignmentSlice';
import { AssignmentTitleInfoModel } from 'api/backofficeApiClient';
import { isCorporateTASelector } from 'redux/selectors/authenticationSelectors';
import AssignmentsFilter, { AssignmentFilters } from '../AssignmentsFilter';
import {
  typeConversionToNumberArray,
  typeConversionToStringArray,
} from 'utils/convertUtils';
import {
  AssignmentFilterTypes,
  AssignmentFilterOptions,
  AssignmentSections,
} from '../enums';
import BaseSearch from 'components/wrappers/BaseSearch';
import AssignmentCard from '../AssignmentCard';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getDateWithoutTime } from 'utils/dateUtils';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';

import styles from '../../client/ClientRequestsSection/ClientRequestsSection.module.less';

type Props = {
  isAdmin?: boolean;
};

export interface AssigmentQueryType {
  section: AssignmentSections;
  query?: string;
  [AssignmentFilterTypes.Customer]?: string[];
  [AssignmentFilterTypes.Company]?: string[];
  [AssignmentFilterTypes.Worker]?: string[];
  [AssignmentFilterTypes.WorkerType]?: WorkerTypeEnum[];
  [AssignmentFilterTypes.TerminationReason]?: AssignmentTerminationReasonTypeEnum[];
  [AssignmentFilterTypes.Status]?: AssignmentStatusTypeEnum[];
  startDate?: string;
  endDate?: string;
}

export default function AssignmentsSection(props: Props) {
  const { isAdmin } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const latestRequestId = useRef(0);
  const infiniteScrollParentRef = useRef(null);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const {
    query,
    section,
    company,
    customer,
    worker,
    workerType,
    termination,
    status,
    startDate,
    endDate,
  } = queryString.parse(location.search);

  const filterStatusesByAssignmentStatusEnum = () => {
    const statuses = typeConversionToNumberArray(status);
    return statuses.filter((num) =>
      Object.values(AssignmentStatusTypeEnum).includes(num),
    );
  };

  const getInitialFilters = (): AssignmentFilters => {
    const filters: AssignmentFilters = {
      [AssignmentFilterTypes.Worker]: typeConversionToStringArray(worker),
      [AssignmentFilterTypes.WorkerType]:
        typeConversionToNumberArray(workerType),
      [AssignmentFilterTypes.TerminationReason]:
        typeConversionToNumberArray(termination),
      [AssignmentFilterTypes.Status]: filterStatusesByAssignmentStatusEnum(),
    };

    if (isAdmin) {
      filters[AssignmentFilterTypes.Company] =
        typeConversionToStringArray(company);
      filters[AssignmentFilterTypes.Customer] =
        typeConversionToStringArray(customer);
      filters[AssignmentFilterTypes.Date] =
        startDate && endDate
          ? {
            startDate: moment(startDate),
            endDate: moment(endDate),
          }
          : undefined;
    }

    if (!isCorporateTA) {
      filters[AssignmentFilterTypes.Customer] =
        typeConversionToStringArray(customer);
    }

    return filters;
  };
  const [filters, setFilters] = useState<AssignmentFilters>(getInitialFilters);
  const [assignments, setAssignments] = useState<AssignmentTitleInfoModel[]>(
    [],
  );
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const queryData: AssigmentQueryType = {
      query,
      section,
      [AssignmentFilterTypes.Status]: filters[
        AssignmentFilterTypes.Status
      ] as AssignmentStatusTypeEnum[],
    };
    if (isAdmin) {
      queryData[AssignmentFilterTypes.Company] =
        filters[AssignmentFilterTypes.Company];
      queryData[AssignmentFilterTypes.Customer] =
        filters[AssignmentFilterTypes.Customer];

      queryData.startDate = filters[AssignmentFilterTypes.Date]?.startDate
        ? filters[AssignmentFilterTypes.Date]?.startDate?.format('MM/DD/YYYY')
        : undefined;

      queryData.endDate = filters[AssignmentFilterTypes.Date]?.endDate
        ? filters[AssignmentFilterTypes.Date]?.endDate?.format('MM/DD/YYYY')
        : undefined;
    }
    if (!isCorporateTA) {
      queryData[AssignmentFilterTypes.Customer] =
        filters[AssignmentFilterTypes.Customer];
    }
    queryData[AssignmentFilterTypes.Worker] =
      filters[AssignmentFilterTypes.Worker];
    queryData[AssignmentFilterTypes.WorkerType] =
      filters[AssignmentFilterTypes.WorkerType];
    queryData[AssignmentFilterTypes.TerminationReason] =
      filters[AssignmentFilterTypes.TerminationReason];

    navigate(`/assignments?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [filters, query, isAdmin, isCorporateTA, section, navigate]);

  useEffect(() => {
    initInfinityScroll();
  }, [filters, query]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setAssignments([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onSetAssignments = useCallback(
    (result: PayloadAction<AdminPagedAssignmentResponseModel>) => {
      const loadedAssignments = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newAssignments = [...assignments, ...loadedAssignments];
      const hasMoreRows = newAssignments.length < totalCount;

      setHasMore(hasMoreRows);
      setAssignments(newAssignments);
      setTotalCount(totalCount);
    },
    [assignments],
  );

  const loadAssignments = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getAssignments({
          requestId,
          pageNumber,
          isAdminRole: isAdmin,
          statuses: filters[AssignmentFilterTypes.Status],
          searchQuery: query ? query.trim() : undefined,
          companyIds: filters[AssignmentFilterTypes.Company],
          customerIds: !filters[AssignmentFilterTypes.Customer]?.includes(
            AssignmentFilterOptions.All,
          )
            ? filters[AssignmentFilterTypes.Customer]
            : [],
          workerIds: !filters[AssignmentFilterTypes.Worker]?.includes(
            AssignmentFilterOptions.All,
          )
            ? filters[AssignmentFilterTypes.Worker]
            : [],
          workerTypes: filters[AssignmentFilterTypes.WorkerType],
          terminationReasons: filters[AssignmentFilterTypes.TerminationReason],
          startDate: filters[AssignmentFilterTypes.Date]?.startDate
            ? getDateWithoutTime(filters[AssignmentFilterTypes.Date]?.startDate)
            : undefined,
          endDate: filters[AssignmentFilterTypes.Date]?.endDate
            ? getDateWithoutTime(filters[AssignmentFilterTypes.Date]?.endDate)
            : undefined,
        }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getAssignments.fulfilled.match(result)) {
          onSetAssignments(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetAssignments, isAdmin, filters, query],
  );

  const handleDownloadAssignmentList = async () => {
    const result = await dispatch(
      getAssignmentListExcel({
        statuses: filters[AssignmentFilterTypes.Status],
        searchQuery: query ? query.trim() : undefined,
        companyIds: filters[AssignmentFilterTypes.Company],
        customerIds: !filters[AssignmentFilterTypes.Customer]?.includes(
          AssignmentFilterOptions.All,
        )
          ? filters[AssignmentFilterTypes.Customer]
          : [],
        workerIds: !filters[AssignmentFilterTypes.Worker]?.includes(
          AssignmentFilterOptions.All,
        )
          ? filters[AssignmentFilterTypes.Worker]
          : [],
        workerTypes: filters[AssignmentFilterTypes.WorkerType],
        terminationReasons: filters[AssignmentFilterTypes.TerminationReason],
        startDate: filters[AssignmentFilterTypes.Date]?.startDate
          ? getDateWithoutTime(filters[AssignmentFilterTypes.Date]?.startDate)
          : undefined,
        endDate: filters[AssignmentFilterTypes.Date]?.endDate
          ? getDateWithoutTime(filters[AssignmentFilterTypes.Date]?.endDate)
          : undefined,
      }),
    );

    if (getAssignmentListExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const getNoAssignmentsMessage = () => {
    if (isAdmin) {
      return `There are no assignments.`;
    }

    return 'You have no assignments. Perhaps it is time to create a new one? Let us know if we can help.';
  };

  const pluralizedAssignments = pluralize(`assignment`, totalCount, true);

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[20, 10]} className={styles.row}>
            <Col xs={24} md={6}>
              <BaseSearch
                searchQuery={query}
                placeholder="Search by assignment name, ID"
              />
            </Col>

            <AssignmentsFilter
              filters={filters}
              onChange={setFilters}
              assignmentStatuses={
                filters[
                AssignmentFilterTypes.Status
                ] as AssignmentStatusTypeEnum[]
              }
            />
          </Row>
          <Row gutter={[20, 10]}>
            <Col xs={24} className={styles.wrapper}>
              <Row gutter={[20, 10]}>
                <Col flex="auto" style={{ alignSelf: 'center' }}>
                  {isInitialDataLoaded && (
                    <>
                      {!!totalCount && (
                        <p className="page-total-count">
                          {pluralizedAssignments}
                        </p>
                      )}
                      {!totalCount && (
                        <span className="no-data-msg">
                          {getNoAssignmentsMessage()}
                        </span>
                      )}
                    </>
                  )}
                </Col>
                {isAdmin && (
                  <>
                    <Col>
                      <Tooltip title="Download Assignment List" trigger="hover">
                        <Button
                          className={styles.btn}
                          onClick={handleDownloadAssignmentList}
                        >
                          <span className="icon icon-xls-file" />
                        </Button>
                      </Tooltip>
                    </Col>

                    <Col>
                      <Button className={styles.btn}>
                        <Link to="/assignment-bulk-import">
                          Import assignments
                        </Link>
                      </Button>
                    </Col>
                  </>
                )}
              </Row>
            </Col>
          </Row>

          {isVisible && (
            <InfiniteScroll
              pageStart={-1}
              loadMore={loadAssignments}
              hasMore={hasMore}
              loader={<Spin key={0} />}
              useWindow={false}
              getScrollParent={() => infiniteScrollParentRef.current}
            >
              <Row gutter={[20, 20]}>
                {assignments.map((assignment) => (
                  <Col
                    key={assignment.assignmentId}
                    xs={24}
                    md={12}
                    className="d-flex"
                  >
                    <AssignmentCard assignment={assignment} />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
